import { Link, useParams } from "react-router-dom";
import useSeo, { SITE_URL } from "@/hooks/useSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug = "sukan" } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const list = category ? articles.filter((a) => a.category === slug) : articles;
  const safeCategory = category ?? categories[0];

  useSeo({
    title: `${safeCategory.name} - 抖漫${safeCategory.name}内容精选`,
    description: safeCategory.description,
    keywords: `抖漫,${safeCategory.name},漫画速看,漫画片段,漫画精选`,
    path: `/category/${slug}`,
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${safeCategory.name} - 抖漫`,
        url: `${SITE_URL}/category/${slug}`,
        description: safeCategory.description,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: safeCategory.name, item: `${SITE_URL}/category/${slug}` },
        ],
      },
    ],
  });

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: safeCategory.name }]} />

      <header className="mt-6">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
          <i className={safeCategory.icon}></i> {safeCategory.name}
        </span>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950">{safeCategory.name}</h1>
        <p className="mt-3 max-w-2xl text-sm md:text-base leading-7 text-foreground-600">{safeCategory.description}</p>
      </header>

      {/* Category switcher */}
      <nav className="mt-8 flex flex-wrap gap-2" aria-label="分类切换">
        {categories.map((c) => (
          <Link
            key={c.slug}
            to={`/category/${c.slug}`}
            aria-current={c.slug === slug ? "page" : undefined}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              c.slug === slug
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            <i className={c.icon}></i> {c.name}
          </Link>
        ))}
      </nav>

      <p className="mt-6 text-sm text-foreground-500">
        共 <strong className="text-primary-600">{list.length}</strong> 篇内容
      </p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {list.map((a) => (
          <ArticleCard key={a.slug} article={a} />
        ))}
      </div>

      {list.length === 0 && (
        <div className="mt-10 rounded-lg border border-background-200 bg-background-50 py-20 text-center">
          <i className="ri-inbox-line text-4xl text-foreground-300"></i>
          <p className="mt-4 text-sm text-foreground-500">该分类暂无内容，去看看其他分类吧。</p>
        </div>
      )}
    </div>
  );
}